import React from 'react';

import styled from 'styled-components';

import InsightCard from './InsightCard';
import { ReviewsSummaryAIInsightsData } from './types';

const Container = styled.div`
  display: flex;
  flex-flow: column;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
  padding-bottom: 10px;
  margin-bottom: 10px;
  gap: 10px;
  border-bottom: 1px solid transparent;
  border-image: linear-gradient(to left, #ff5b97, #615ffd) 1;
  border-image-slice: 1;
  width: fit-content;
`;

const Title = styled.div`
  font-size: 22px;
  font-family: var(--font-bold);
`;

const Summary = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 20px;
  line-height: 1.5;
`;

const AIInsights = styled.div`
  display: flex;
  flex-flow: row;
  gap: 20px;
`;

const AIInsightsLabel = styled.div`
  font-size: 14px;
  color: var(--text-primary);
  font-family: var(--font-bold);
  margin-bottom: 3px;
`;

const Strengths = styled.div`
  display: flex;
  flex-flow: column;
  gap: 10px;
  flex: 1;
`;

const Weaknesses = styled.div`
  display: flex;
  flex-flow: column;
  gap: 10px;
  flex: 1;
`;

const NoDataText = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  text-align: center;
  padding: 20px;
`;


type Props = {
  aiInsights: ReviewsSummaryAIInsightsData | null;
};

const AISummary = ({ aiInsights }: Props) => {
  if (!aiInsights) {
    return null;
  }

  const {
    summary,
    strengths = [],
    weaknesses = [],
    unique_strengths = [],
    unique_weaknesses = [],
  } = aiInsights;

  const hasData =
    summary ||
    strengths.length > 0 ||
    weaknesses.length > 0 ||
    unique_strengths.length > 0 ||
    unique_weaknesses.length > 0;

  if (!hasData) {
    return (
      <Container>
        <Title>AI Summary</Title>
        <NoDataText>No AI insights available for this community yet.</NoDataText>
      </Container>
    );
  }

  const allStrengths = [
    ...unique_strengths.map((item) => ({ item, isUnique: true })),
    ...strengths.map((item) => ({ item, isUnique: false })),
  ];

  const allWeaknesses = [
    ...unique_weaknesses.map((item) => ({ item, isUnique: true })),
    ...weaknesses.map((item) => ({ item, isUnique: false })),
  ];

  return (
    <Container>
      <TitleContainer>
        <img src="/icons/ai-blob.svg" alt="ai-blob" height="30px" width="30px" />
        <Title>AI Summary</Title>
      </TitleContainer>
      {summary && <Summary data-testid="rs-widget-overall-summary-text">{summary}</Summary>}
      <AIInsights>
        <Strengths>
          <AIInsightsLabel data-testid="rs-widget-strengths-title">Strengths</AIInsightsLabel>
          {allStrengths.length > 0 ? (
            allStrengths.map(({ item, isUnique }, index) => (
              <InsightCard
                key={`strength-${index}`}
                item={item}
                index={index}
                type="strengths"
                isUnique={isUnique}
              />
            ))
          ) : (
            <NoDataText>No strengths identified</NoDataText>
          )}
        </Strengths>
        <Weaknesses>
          <AIInsightsLabel data-testid="rs-widget-weaknesses-title">Weaknesses</AIInsightsLabel>
          {allWeaknesses.length > 0 ? (
            allWeaknesses.map(({ item, isUnique }, index) => (
              <InsightCard
                key={`weakness-${index}`}
                item={item}
                index={index}
                type="weaknesses"
                isUnique={isUnique}
              />
            ))
          ) : (
            <NoDataText>No weaknesses identified</NoDataText>
          )}
        </Weaknesses>
      </AIInsights>
    </Container>
  );
};

export default AISummary;
